import React from "react";
import { motion } from "framer-motion";
import {
  Users,
  CheckCircle,
  Globe,
  Award,
  Mail,
  Star,
  Target,
  Leaf,
  Shield,
  FileCheck,
  Building2,
  Scale,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const SEDEX_SEMETA_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0f2d4d] via-[#1a4d7a] to-[#1a5fa8] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <Users size={18} className="text-blue-300" />
            <span>Social Compliance Management</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-300 via-cyan-300 to-indigo-300">
            SEDEX / SEMETA
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Ethical Sourcing and Supply Chain Transparency for Global Compliance
          </p>
        </motion.div>
      </section>

      {/* What is SEDEX/SEMETA */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is Social Compliance / SEDEX / SEMETA?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              SEDEX (Supplier Ethical Data Exchange) is a global platform for
              ethical sourcing and social compliance management. SEMETA (SEDEX
              Members Ethical Trade Audit) is an audit tool that assesses
              compliance with ethical standards including labor practices,
              health and safety, environmental sustainability, and business
              ethics.
            </p>
            <p>
              Social compliance certification verifies that a company and its
              supply chain adhere to standards related to labor practices,
              health and safety, environmental impacts, and ethical business
              operations, ensuring fair treatment of workers globally.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of SEDEX / SEMETA
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Ethical Business Practices",
                desc: "Ensures ethical sourcing and fair labor practices throughout operations",
                icon: Scale,
              },
              {
                title: "Worker Welfare and Rights",
                desc: "Protects workers' rights with fair treatment and safe conditions",
                icon: Users,
              },
              {
                title: "Supply Chain Transparency",
                desc: "Provides visibility into supply chain practices and compliance",
                icon: Globe,
              },
              {
                title: "Health and Safety",
                desc: "Ensures compliance with health and safety regulations",
                icon: Shield,
              },
              {
                title: "Environmental Responsibility",
                desc: "Evaluates environmental impact and sustainable practices",
                icon: Leaf,
              },
              {
                title: "Human Rights Protection",
                desc: "Ensures no child labor, forced labor, or discrimination",
                icon: Award,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-blue-600 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* SEDEX and SEMETA Explained */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Understanding SEDEX and SEMETA
          </h2>
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              SEDEX focuses on four key pillars of social compliance:
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Labor Standards - Fair wages and working conditions",
                "Health & Safety - Safe working environments",
                "The Environment - Sustainable and responsible practices",
                "Business Ethics - Transparent and ethical conduct",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-lg text-gray-700">
              SEMETA audits assess compliance with these standards and help
              companies improve working conditions across their supply chain.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Why SEDEX / SEMETA Matters
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Risk Mitigation - Reduce compliance risks in supply chain",
              "Regulatory Compliance - Meet international labor standards",
              "Improved Supplier Relationships - Ensure transparency with partners",
              "Enhanced Brand Reputation - Demonstrate CSR commitment",
              "Market Access - Meet ethical sourcing requirements of buyers",
              "Consumer Trust - Build confidence through ethical practices",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Benefits of SEDEX / SEMETA Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Global Supply Chain Transparency across operations",
              "Consumer Confidence through ethical practice certification",
              "Risk Reduction in labor and compliance violations",
              "Market Access and competitive advantage",
              "Improved Brand Image with sustainability focus",
              "Regulatory Compliance with labor standards",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-blue-100 to-indigo-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-blue-700 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Advantages of SEDEX / SEMETA Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Enhanced Supply Chain Transparency and visibility",
              "Regulatory Compliance with international labor laws",
              "Consumer Trust and brand reputation improvement",
              "Increased Market Access with ethical sourcing",
              "Risk Reduction from supply chain violations",
              "Worker Welfare and human rights protection",
              "Supplier Accountability and engagement",
              "Competitive Advantage in ethical markets",
            ].map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries Using SEDEX / SEMETA
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Retail and Fashion",
              "Manufacturing and Processing",
              "Food and Beverage",
              "Electronics and Technology",
              "Consumer Goods",
              "Supply Chain and Logistics",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg"
              >
                <Building2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Achieve SEDEX / SEMETA Certification
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "SEDEX Registration",
                desc: "Register and complete SEDEX self-assessment",
              },
              {
                step: 2,
                title: "Self-Assessment Completion",
                desc: "Evaluate current ethical practices and supply chain compliance",
              },
              {
                step: 3,
                title: "SEDEX or SEMETA Audit",
                desc: "Engage accredited auditor for on-site assessment",
              },
              {
                step: 4,
                title: "Action Plan Development",
                desc: "Create corrective action plan based on audit findings",
              },
              {
                step: 5,
                title: "Improvements Implementation",
                desc: "Implement worker welfare and sustainability improvements",
              },
              {
                step: 6,
                title: "Ongoing Monitoring",
                desc: "Regular monitoring and periodic audits for continued compliance",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for SEDEX / SEMETA?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions provides expert SEDEX and SEMETA consultancy
              services, guiding businesses through certification to ensure
              ethical trade and social responsibility.
            </p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "SEDEX Membership Registration",
                "Self-Assessment and Gap Analysis",
                "Audit Preparation and Coordination",
                "Action Plan Development",
                "Employee Training Services",
                "Monitoring and Reporting Support",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              We help businesses achieve SEDEX and SEMETA certification for
              ethical sourcing, fair labor practices, and sustainable supply
              chains.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default SEDEX_SEMETA_Details;
